import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRequestData } from '../helpers/helpers';
import { t } from '../locale';

type RequestStatusView = 'confirming' | 'finished' | null;

function getRouteIds(pathname: string) {
  const pathSegments = pathname.split('/');
  return {
    companyId: pathSegments[1],
    requestId: pathSegments[2],
  };
}

export default function RequestStatusPage() {
  const [statusView, setStatusView] = useState<RequestStatusView>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, requestId } = getRouteIds(location.pathname);

  useEffect(() => {
    const loadStatus = async () => {
      if (!companyId || !requestId) {
        return;
      }

      try {
        const data = await getRequestData(companyId, requestId);

        if (data?.status === 'SUBMITTED' || data?.status === 'PENDING_PAYMENT') {
          setStatusView('confirming');
          return;
        }

        if (data?.status === 'SUCCESS') {
          setStatusView('finished');
          return;
        }

        navigate(`/${companyId}/${requestId}/p`, { replace: true });
      } catch (error) {
        console.error('Error while loading request status page:', error);
      }
    };

    loadStatus();
  }, [companyId, navigate, requestId]);

  if (!statusView) {
    return null;
  }

  const confirming = statusView === 'confirming';
  const title = confirming
    ? t('identificationInProgress')
    : t('identificationCompleted');
  const description = confirming
    ? t('identificationInProgressDesc')
    : t('identificationCompletedDesc');
  const accentColor = confirming ? 'var(--bs-warning)' : 'var(--bs-success)';
  const iconClasses = confirming
    ? 'rounded-circle bg-warning-subtle text-warning'
    : 'rounded-circle bg-success-subtle text-success';

  return (
    <div
      className='min-vh-100 d-flex align-items-center justify-content-center'
      style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)' }}
    >
      <div className='container px-3 px-sm-4'>
        <div className='row justify-content-center'>
          <div className='col-12 col-md-10 col-lg-8 col-xl-6'>
            <div className='card border-0 shadow-lg rounded-4 overflow-hidden'>
              <div style={{ height: 6, backgroundColor: accentColor }} />
              <div className='card-body p-4 p-sm-5 text-center'>
                <div className='d-flex justify-content-center mb-3'>
                  <div
                    className={`${iconClasses} d-inline-flex align-items-center justify-content-center`}
                    style={{ width: 90, height: 90, border: `3px solid ${accentColor}` }}
                  >
                    {confirming ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='currentColor' viewBox='0 0 16 16' aria-hidden='true'>
                        <path d='M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z' />
                        <path d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0' />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='currentColor' viewBox='0 0 16 16' aria-hidden='true'>
                        <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.08-.02l3.992-4.99a.75.75 0 0 0-.012-1.05z' />
                      </svg>
                    )}
                  </div>
                </div>
                <h2 className='mb-2 fw-semibold'>{title}</h2>
                <p className='text-muted mb-4'>{description}</p>
                <div className='small text-secondary border-top pt-3'>
                  Request ID: <span className='fw-semibold'>{requestId}</span>
                </div>
              </div>
            </div>
            <p className='text-center text-muted small mt-3 mb-0'>{t('footerTagline')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
